// src/pages/HomePage.jsx
//
// Sivakasi Pattasu Mart — home page (short & colourful).
//
//   1. Hero — festive BIG Diwali Sale banner + Shop / WhatsApp buttons
//   2. Shop by category — colourful tiles
//   3. Hot picks — product rail
//   4. Call / WhatsApp band

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { shopAPI } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import { ProductSkeleton, fmt, priceOf } from '../components/ui';
import FireworksCanvas from '../components/fx/FireworksCanvas';
import DiwaliBanner from '../components/fx/DiwaliBanner';
import {
  SHOP_NAME, SHOP_PHONES, ADMIN_WHATSAPP_NUMBER, MIN_ORDER_TN, MIN_ORDER_OTHER, OFFER_PERCENT,
} from '../config/tenant';
import '../components/fx/fx.css';
import './HomePage.css';

/* Pick an emoji for a category from its name */
const CAT_EMOJI = [
  [/sparkl|kambi|matha/i, '✨'], [/rocket/i, '🚀'], [/pot|flower/i, '🌋'],
  [/chakk|chakr|wheel|spinner/i, '🌀'], [/bomb|atom|hydro/i, '💥'],
  [/one sound|lakshmi|sound|kuruvi|bijili|deluxe|thandar/i, '🧨'],
  [/wala|garland|chain/i, '🎊'], [/shot|aerial|sky|fancy|multi|repeat/i, '🎆'],
  [/gift|combo|box|family/i, '🎁'], [/kid|child|toy|novel/i, '🧸'],
  [/smoke|colour|color/i, '🌈'], [/twinkl|star/i, '⭐'], [/pencil|candle/i, '🕯️'],
];
const emojiFor = (name = '') => {
  const hit = CAT_EMOJI.find(([re]) => re.test(name));
  return hit ? hit[1] : '🎇';
};

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: .5, delay: d, ease: [.16, 1, .3, 1] },
});

export default function HomePage() {
  const [products,    setProducts]    = useState([]);
  const [featured,    setFeatured]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [globalOffer, setGlobalOffer] = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      shopAPI.getFeatured(),
      shopAPI.getCategories(),
      shopAPI.getProducts({ sort: 'created_at', order: 'DESC', limit: 12, page: 1 }),
    ]).then(([f, c, p]) => {
      if (!alive) return;
      const fd = f.status === 'fulfilled' ? f.value.data : {};
      const pd = p.status === 'fulfilled' ? p.value.data : {};
      setFeatured(fd.data || []);
      setProducts(pd.data || []);
      setGlobalOffer(fd.global_offer || pd.global_offer || null);
      if (c.status === 'fulfilled') {
        setCategories((c.value.data.data || []).filter(x => Number(x.product_count) > 0));
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const bestPct = Math.max(0, ...products.map(p => priceOf(p).pct || 0));
  const pct   = OFFER_PERCENT || (globalOffer?.value ? Math.round(globalOffer.value) : (bestPct || null));
  const picks = (featured.length ? featured : products).slice(0, 10);
  const phone = SHOP_PHONES[0];
  const wa    = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi ${SHOP_NAME}! I'd like to place a crackers order enquiry 🎆`)}`;

  return (
    <div className="page-top fh">

      {/* ═══ HERO — festive Diwali sale banner ═══ */}
      <DiwaliBanner variant="hero" pct={pct || 80}>
        <div className="fh-hero-cta">
          <Link to="/products" className="fh-btn fh-btn-gold">
            Shop Now <ArrowRightIcon className="h-5 w-5" />
          </Link>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="fh-btn fh-btn-wa">
            💬 WhatsApp
          </a>
        </div>
        <p className="fh-hero-min">
          Min order {fmt(MIN_ORDER_TN)} (TN) · {fmt(MIN_ORDER_OTHER)} (Other states) · All India orders
        </p>
      </DiwaliBanner>

      {/* ═══ CATEGORIES ═══ */}
      {categories.length > 0 && (
        <section className="fh-section">
          <div className="fh-wrap">
            <motion.div className="fh-head" {...fade()}>
              <h2 className="fh-h2">Shop by category</h2>
              <Link to="/products" className="fh-more">View all <ArrowRightIcon className="h-4 w-4" /></Link>
            </motion.div>

            <div className="fh-cats">
              {categories.slice(0, 12).map((c, i) => (
                <motion.div key={c.id} {...fade(Math.min(i * .04, .3))}>
                  <Link to={`/products?category_id=${c.id}`} className={`fh-cat fh-cat-${i % 8}`}>
                    <span className="fh-cat-emoji" aria-hidden="true">{emojiFor(c.name)}</span>
                    <span className="fh-cat-name">{c.name}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ HOT PICKS ═══ */}
      {(loading || picks.length > 0) && (
        <section className="fh-section fh-picks">
          <div className="fh-wrap">
            <motion.div className="fh-head" {...fade()}>
              <h2 className="fh-h2">🔥 Hot picks</h2>
              <Link to="/products" className="fh-more">See all <ArrowRightIcon className="h-4 w-4" /></Link>
            </motion.div>

            <div className="fh-rail no-scrollbar">
              {loading
                ? [...Array(5)].map((_, i) => <div key={i} className="fh-rail-item"><ProductSkeleton /></div>)
                : picks.map((p, i) => (
                    <div key={p.id} className="fh-rail-item"><ProductCard product={p} index={i} /></div>
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ CONTACT ═══ */}
      <section className="fh-contact">
        <FireworksCanvas density={0.6} />
        <div className="fh-wrap fh-contact-inner">
          <motion.div {...fade()}>
            <h2 className="fh-h2 fh-light">Order now — call or WhatsApp</h2>
            <div className="fh-contact-actions">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="fh-btn fh-btn-white">📞 {phone}</a>
              <a href={wa} target="_blank" rel="noopener noreferrer" className="fh-btn fh-btn-wa">💬 WhatsApp</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
